import { Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { memo } from "react";

const CHECK_GLYPH = "✓";

type HeroSectionProps = {
  readonly title: string;
  readonly subtitle: string;
  readonly perks: readonly string[];
  readonly heroImage?: string;
  /**
   * Brand accent for the perk check marks, e.g. "text-violet-400" or
   * "text-blue-300". Defaults to a neutral brand blue. Lets each brand
   * tint the perks without touching layout.
   */
  readonly accentClassName?: string;
};

function HeroSection({ title, subtitle, perks, heroImage, accentClassName = "text-blue-600 dark:text-blue-300" }: HeroSectionProps) {
  return (
    <section className="mx-auto max-w-[1600px]" data-testid="HeroSection">
      {/* IMAGE BAND — fixed height, holds only title + subtitle (predictable
          height) so the portrait is never pushed off-screen by long perks. */}
      <div className="relative h-[300px] overflow-hidden md:h-[440px]">
        {heroImage ? (
          <img
            src={heroImage}
            alt=""
            draggable={false}
            className="absolute inset-0 h-full w-full object-cover object-[center_15%]"
            data-testid="HeroSection-Image"
          />
        ) : null}

        {/* Theme fade: melts the image into the page bg. */}
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,transparent_0%,transparent_35%,rgba(255,255,255,0.55)_70%,white_100%)] dark:bg-[linear-gradient(to_bottom,transparent_0%,transparent_30%,rgba(6,6,8,0.5)_68%,#060608_100%)]" />

        {/* Legibility scrim behind the copy only. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[55%] bg-[linear-gradient(to_top,white,rgba(255,255,255,0.55)_35%,transparent)] dark:bg-[linear-gradient(to_top,rgba(0,0,0,0.78),rgba(0,0,0,0.35)_45%,transparent)]" />

        <div className="absolute inset-x-0 bottom-0 mx-auto flex max-w-[1080px] flex-col items-start gap-1.5 px-5 pb-5 md:px-10 md:pb-8">
          <Text
            variant={TextVariants.HEADING_2_BOLD}
            className="text-balance font-extrabold leading-[1.05] tracking-tight text-gray-900 dark:text-white dark:[text-shadow:0_2px_18px_rgba(0,0,0,0.55)] md:text-[52px]"
          >
            {title}
          </Text>
          <Text
            variant={TextVariants.PARAGRAPH_MEDIUM_REGULAR}
            className="text-gray-600 dark:text-white/85 dark:[text-shadow:0_1px_10px_rgba(0,0,0,0.5)] md:text-[18px]"
          >
            {subtitle}
          </Text>
        </div>
      </div>

      {/* PERKS BAND — lives BELOW the image in normal flow, so however many
          perks (or however long) they never cover the portrait. Wraps onto
          multiple lines so every perk is visible without scrolling. */}
      {perks.length > 0 ? (
        <ul className="m-0 flex list-none flex-wrap gap-2 px-5 pt-2 md:justify-center md:px-10 md:pt-3">
          {perks.map((perk) => (
            <li
              key={perk}
              className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-black/[0.04] py-1.5 pl-2 pr-3.5 text-[13px] font-semibold text-gray-800 dark:border-white/10 dark:bg-white/[0.06] dark:text-white/90 md:text-sm"
            >
              <span
                aria-hidden="true"
                className={`grid h-5 w-5 shrink-0 place-items-center rounded-full bg-current/10 text-[11px] font-bold ${accentClassName}`}
              >
                {CHECK_GLYPH}
              </span>
              {perk}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export default memo(HeroSection);
